<template>
  <div>
    <breadcrumb-header
      :items="breadcrumbs"
      help-link="/docs/publisher/managing-shifts/locations"
    />
    <v-tabs
      v-model="tab"
    >
      <smpw-tab
        value="locations"
        :active="tab === 'locations'"
        :text="t('location.title', 2)"
      />
      <smpw-tab
        value="stores"
        :active="tab === 'stores'"
        :text="t('store.title', 2)"
      />
      <smpw-tab
        value="map"
        :active="tab === 'map'"
        :text="t('location.global_map')"
      />
    </v-tabs>
    <v-window
      v-model="tab"
      :touch="false"
    >
      <v-window-item value="locations">
        <div
          data-testid="locations-filter"
        >
          <v-icon
            color="grey-darken-1"
            icon="fas fa-filter"
            class="me-2 mb-2"
            size="x-small"
          />
          <v-btn
            v-for="day in days"
            :key="day.value"
            class="me-1 mb-2 text-primary-darken-1"
            :class="{ 'opacity-inactive': !day.active }"
            :data-testid="`locations-day-filter-${day.value}`"
            :text="day.name"
            size="small"
            :color="hasDayFilter(day) ? 'primary-lighten-1 text-primary-darken-1' : 'light-grey text-font'"
            @click="toggleDayFilter(day)"
          />
          <v-icon
            color="grey-darken-1"
            icon="fas fa-minus"
            class="me-1 mb-2"
            size="x-small"
          />
          <v-btn
            class="me-1 mb-2 text-primary-darken-1"
            :color="shouldShowAllDays ? 'primary-lighten-1 text-primary-darken-1' : 'light-grey text-font'"
            :text="t('actions.show_all')"
            size="small"
            @click="clearDayFilter"
          />
        </div>
        <v-row
          v-if="locations.length > 0"
        >
          <v-col
            v-for="location in locations"
            :key="location.id"
            :md="6"
            :lg="4"
            :cols="12"
          >
            <v-card
              class="fill-height mx-1"
            >
              <v-img
                :src="getHeroImage(location)"
                :alt="location.name"
                max-height="200px"
                class="align-end"
                cover
              />
              <v-card-title class="text-primary-darken-1">
                {{ location.name }}
              </v-card-title>

              <v-card-text>
                <v-chip
                  v-for="day in days"
                  :key="day.value"
                  size="small"
                  class="ms-1 mb-1"
                  variant="flat"
                  :text="day.name"
                  :color="location.weekdays?.includes(day.value) ? 'primary-darken-1' : 'font'"
                  :disabled="!location.weekdays?.includes(day.value)"
                />
                <v-btn
                  :to="getLocationDetailsUrl(location.id)"
                  color="primary"
                  :text="t('location.view_details')"
                  block
                  size="large"
                  class="mb-2 mt-1"
                />
                <v-btn
                  :disabled="!location?.store?.active"
                  :to="getStoreDetailsUrl(location?.store?.id)"
                  color="primary"
                  variant="outlined"
                  :elevation="2"
                  size="large"
                  block
                  :text="`${t('store.view')} - ${location?.store?.name}`"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-alert
          v-else
          color="primary"
          :text="t('location.none_found')"
        />
      </v-window-item>
      <v-window-item value="stores">
        <v-row
          v-if="allStores.length > 0"
        >
          <v-col
            v-for="store in allStores"
            :key="store.id"
            :md="6"
            :lg="4"
            :cols="12"
          >
            <v-card
              class="my-4"
              :data-testid="`store-${store.id}`"
            >
              <v-img
                max-height="200px"
                class="align-end"
                cover
                :src="getStoreHeroImage(store)"
                :alt="store.name"
              />
              <v-card-title>
                {{ store.name }}
              </v-card-title>
              <v-card-text>
                <v-btn
                  :to="getStoreDetailsUrl(store.id)"
                  color="primary"
                  :text="t('store.view_details')"
                  block
                  size="large"
                  class="mb-4"
                />
                <v-divider class="mb-2" />
                <v-card-subtitle class="text-body-large">
                  {{ t('store.locations_using') }}
                </v-card-subtitle>
                <v-list>
                  <v-list-item
                    v-for="location in (store.locations ?? []).filter((location) => location.active)"
                    :key="location.id"
                    :to="getLocationDetailsUrl(location.id)"
                    :disabled="!location.active"
                  >
                    <span class="text-body-large">
                      {{ location.name }}
                    </span>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-alert
          v-else
          color="primary"
          :text="t('store.none_found')"
        />
      </v-window-item>
      <v-window-item value="map">
        <global-locations-map />
      </v-window-item>
    </v-window>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useDataTableUtilities } from '@/composables';
import { useErrorStore } from '@/stores/errors';
import GlobalLocationsMap from './GlobalLocationsMap.vue';

const { t } = useI18n();
const { tab, watchString } = useDataTableUtilities();
watchString(tab, 'tab', 'locations');

const { showSnackMessage, handleError } = useErrorStore();
const allLocations = ref<App.Models.Location[]>([]);
const allStores = ref<App.Models.Store[]>([]);

const breadcrumbs = computed(() => [
  {
    title: t('location.title', 2),
  },
]);
const { watchArray, getNumberArrayFromQuery } = useDataTableUtilities();
const route = useRoute();
const selectedDays = ref<number[]>(getNumberArrayFromQuery(route.query?.days, []) ?? []);
watchArray(selectedDays, 'days', [], true);
const shouldShowAllDays = computed(() => selectedDays.value.length === 0);
function clearDayFilter() {
  selectedDays.value = [];
}

/** One weekday chip in the filter bar. */
interface WeekDayFilter {
  name: string;
  value: number;
  active: boolean;
}

function hasDayFilter(day: WeekDayFilter): boolean {
  return selectedDays.value.includes(day.value);
}

function toggleDayFilter(day: WeekDayFilter): void {
  const val = day.value;
  if (selectedDays.value.includes(val)) {
    selectedDays.value = selectedDays.value.filter((d) => d !== val);
  }
  else {
    selectedDays.value = [...selectedDays.value, val];
  }
}

const days = ref<WeekDayFilter[]>([
  {
    name: t('dow.1'),
    value: 1,
    active: true,
  },
  {
    name: t('dow.2'),
    value: 2,
    active: true,
  },
  {
    name: t('dow.3'),
    value: 3,
    active: true,
  },
  {
    name: t('dow.4'),
    value: 4,
    active: true,
  },
  {
    name: t('dow.5'),
    value: 5,
    active: true,
  },
  {
    name: t('dow.6'),
    value: 6,
    active: true,
  },
  {
    name: t('dow.7'),
    value: 7,
    active: true,
  },
]);

function getLocationDetailsUrl(id: number): string {
  return `/locations/${id}`;
}
function getStoreDetailsUrl(id: number | undefined): string {
  return `/stores/${id}`;
}
function getHeroImage(location: App.Models.Location): string {
  return location?.hero_image?.signed_file_url ?? '/storage/images/defaut_location_hero.jpg';
}

const locations = computed(() => {
  if (selectedDays.value.length === 0) {
    return allLocations.value;
  }

  return allLocations.value.filter(
    (item) => item?.weekdays?.filter((value) => selectedDays?.value?.includes(value)).length > 0,
  );
});

async function fetchLocations() {
  try {
    const res = await axios.get('/api/locations', {
      params: {
        active: true,
        inc: ['weekdays', 'heroImage', 'store'],
        sort: 'name',
      },
    });
    allLocations.value = res.data.data;
  }
  catch (error) {
    console.error(error);
    allLocations.value = [];
    showSnackMessage(t('location.cannot_load'));
  }
}
async function fetchStores() {
  try {
    const res = await axios.get('/api/stores', {
      params: {
        active: true,
        inc: ['weekdays', 'images', 'locations'],
        sort: 'name',
      },
    });
    allStores.value = res.data.data;
  }
  catch (error) {
    handleError(error, t('store.cannot_load'));
    allStores.value = [];
  }
}

fetchLocations();
fetchStores();

function getStoreHeroImage(store: App.Models.Store): string {
  return store?.images?.[0]?.signed_file_url ?? '/storage/images/defaut_store_hero.jpg';
}
</script>
